import { z } from 'zod';
import {
  ADDRESS_MESSAGES,
  DEFAULT_COUNTRY,
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
  DEFAULT_TIMEZONE,
  normalizeRegion,
  SUPPORTED_COUNTRY_CODES,
  type SupportedCountry,
} from './address';
import { baseRow, email, hexColor, id, isoDate, listInput, nonEmpty, optionalText, partialOf, slug } from './common';
import { industryTemplateId } from './templates';

// ---------------------------------------------------------------------------
// Roles and permissions
// ---------------------------------------------------------------------------

/**
 * MarketingBee roles. `rep` is the default staff role (Hive `member` maps to it). Managers run
 * campaigns and see every rep's pipeline; reps work their own records.
 */
export const ROLES = ['owner', 'admin', 'manager', 'rep'] as const;
export const role = z.enum(ROLES);
export type Role = z.infer<typeof role>;

export const MEMBER_ROLES = ROLES;
export const memberRole = role;
export type MemberRole = Role;

export const DEFAULT_STAFF_ROLE: Role = 'rep';

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Sales manager',
  rep: 'Sales rep',
};

export const MEMBERSHIP_STATUSES = ['active', 'suspended'] as const;
export const membershipStatus = z.enum(MEMBERSHIP_STATUSES);

export const INVITATION_STATUSES = ['pending', 'accepted', 'revoked', 'expired'] as const;
export const invitationStatus = z.enum(INVITATION_STATUSES);

/** Resources the permission engine knows about. */
export const RESOURCES = [
  'workspace',
  'settings',
  'billing',
  'member',
  'audit',
  'export',
  'company',
  'contact',
  'list',
  'pipeline',
  'deal',
  'activity',
  'task',
  'report',
  'brand',
  'content',
  'channel',
  'link',
  'subscriber',
  'sequence',
  'integration',
  'webhook',
  'api_key',
] as const;
export const resource = z.enum(RESOURCES);
export type Resource = z.infer<typeof resource>;

export const ACTIONS = ['view', 'create', 'update', 'delete', 'export', 'manage'] as const;
export const action = z.enum(ACTIONS);
export type Action = z.infer<typeof action>;

export type Permission = `${Resource | '*'}:${Action | '*'}` | '*';

export const permissionSet = z.object({
  allow: z.array(z.string()).default([]),
  deny: z.array(z.string()).default([]),
  /** Restrict update/delete to records the user owns or is assigned to. */
  ownRecordsOnly: z.boolean().optional(),
});
export type PermissionSet = z.infer<typeof permissionSet>;

// ---------------------------------------------------------------------------
// Workspace
// ---------------------------------------------------------------------------

export const brandingSchema = z.object({
  logoKey: z.string().max(1024).nullable().optional(),
  primaryColor: hexColor.optional(),
});
export type Branding = z.infer<typeof brandingSchema>;

export const onboardingSchema = z.object({
  company: z.boolean().default(false),
  brand: z.boolean().default(false),
  channels: z.boolean().default(false),
  firstPlan: z.boolean().default(false),
  dismissed: z.boolean().default(false),
});
export type Onboarding = z.infer<typeof onboardingSchema>;

/**
 * Workspace settings, stored sparse as JSON and read through this schema so settings saved before a
 * field existed get its default. Defaults are Canadian (CAD, en-CA, Toronto time).
 */
export const workspaceSettingsSchema = z.object({
  timezone: z.string().max(64).default(DEFAULT_TIMEZONE),
  currency: z.string().trim().toUpperCase().length(3).default(DEFAULT_CURRENCY),
  locale: z.string().max(16).default(DEFAULT_LOCALE),
  country: z.string().trim().toUpperCase().length(2).default(DEFAULT_COUNTRY),
  region: z.string().trim().toUpperCase().max(2).nullable().optional(),
  industry: z.string().trim().max(80).nullable().optional(),
  website: z.string().trim().max(255).nullable().optional(),
  onboarding: onboardingSchema.default(onboardingSchema.parse({})),
});
export type WorkspaceSettings = z.infer<typeof workspaceSettingsSchema>;

export function parseSettings(raw: unknown): WorkspaceSettings {
  return workspaceSettingsSchema.parse(raw ?? {});
}

const locationFields = {
  country: z.string().trim().toUpperCase().pipe(z.enum(SUPPORTED_COUNTRY_CODES as [SupportedCountry, ...SupportedCountry[]])).optional(),
  region: z.string().trim().max(60).optional(),
};

function locateRegion(country: SupportedCountry | undefined, region: string): { country: SupportedCountry; region: string } | null {
  for (const c of country ? [country] : (['CA', 'US'] as const)) {
    const code = normalizeRegion(c, region);
    if (code) return { country: c, region: code };
  }
  return null;
}

type Location = { country?: SupportedCountry; region?: string };

function normalizeLocation<V extends Location>(v: V): V {
  if (!v.region) return v;
  const found = locateRegion(v.country, v.region);
  return found ? { ...v, ...found } : v;
}

function checkLocation(v: Location, ctx: z.RefinementCtx): void {
  if (v.region && !locateRegion(v.country, v.region)) {
    const message =
      v.country === 'CA' ? ADDRESS_MESSAGES.CA.region : v.country === 'US' ? ADDRESS_MESSAGES.US.region : 'Choose a Canadian province or US state';
    ctx.addIssue({ code: 'custom', path: ['region'], message });
  }
}

export const SALES_PLAN_IDS = ['mkt_starter', 'mkt_growth'] as const;
export const salesPlanId = z.enum(SALES_PLAN_IDS);
export type SalesPlanId = z.infer<typeof salesPlanId>;

export const signUpInput = z
  .object({
    name: nonEmpty(120),
    email,
    password: z.string().min(10, 'At least 10 characters').max(128),
    workspaceName: nonEmpty(120),
    plan: salesPlanId.default('mkt_starter'),
    ...locationFields,
  })
  .overwrite(normalizeLocation)
  .superRefine(checkLocation);
export type SignUpInput = z.infer<typeof signUpInput>;

export const createWorkspaceInput = z
  .object({
    name: nonEmpty(120),
    slug: slug.optional(),
    plan: salesPlanId.default('mkt_starter'),
    /** Industry template to add (pipeline + starter sequences). Optional: skip for the generic setup. */
    template: industryTemplateId.optional(),
    ...locationFields,
  })
  .overwrite(normalizeLocation)
  .superRefine(checkLocation);
export type CreateWorkspaceInput = z.infer<typeof createWorkspaceInput>;

export const updateWorkspaceInput = z.object({ name: nonEmpty(120).optional() });

export const updateSettingsInput = partialOf(
  workspaceSettingsSchema.omit({ onboarding: true }),
).extend({
  onboarding: onboardingSchema.partial().optional(),
});
export type UpdateSettingsInput = z.infer<typeof updateSettingsInput>;

// ---------------------------------------------------------------------------
// Users, memberships, invitations
// ---------------------------------------------------------------------------

export const membershipSchema = baseRow.extend({
  userId: id,
  role,
  status: membershipStatus,
  title: z.string().nullable(),
});
export type Membership = z.infer<typeof membershipSchema>;

export const updateMemberInput = z.object({
  id,
  role: role.optional(),
  title: z.string().trim().max(120).nullable().optional(),
  status: membershipStatus.optional(),
});
export type UpdateMemberInput = z.infer<typeof updateMemberInput>;

export const createInvitationInput = z.object({
  email,
  role: role.exclude(['owner']),
  message: optionalText(1000),
});
export type CreateInvitationInput = z.infer<typeof createInvitationInput>;

export const acceptInvitationInput = z.object({ token: z.string().min(16).max(256) });

export const listInvitationsInput = listInput.extend({ status: invitationStatus.optional() });

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export const ACTOR_TYPES = ['user', 'api_key', 'system', 'integration', 'hive'] as const;
export const actorType = z.enum(ACTOR_TYPES);
export type ActorType = z.infer<typeof actorType>;

export const listAuditEventsInput = listInput.extend({
  entityType: z.string().max(64).optional(),
  entityId: id.optional(),
  action: z.string().max(128).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type ListAuditEventsInput = z.infer<typeof listAuditEventsInput>;

export const workspaceSummarySchema = z.object({
  id,
  name: z.string(),
  slug: z.string(),
  role,
});
export type WorkspaceSummary = z.infer<typeof workspaceSummarySchema>;

export const updateProfileInput = z.object({ name: nonEmpty(120).optional() });

export { email as emailSchema };
